import React, { useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { EyeCrossedIcon, EyeIcon, MoreIcon } from '@modernloop/shared/icons';
import { Divider, IconButton, ListItemIcon, ListSubheader, Menu, MenuItem, Tooltip } from '@mui/material';

import {
  InterviewType,
  MoreMenuOptions_JobStageInterviewFragment,
  MoreMenuOptions_JobStageInterviewGroupsFragment,
} from 'src/generated/mloop-graphql';

type MenuOption = {
  id: string;
  value?: string;
  isDivider?: boolean;
  isSubheader?: boolean;
  disabled?: boolean;
  icon?: JSX.Element;
};

export enum InterviewMenuOptions {
  MOVE_UP = 'move_up',
  MOVE_DOWN = 'move_down',
  GROUP_WITH_ABOVE = 'group_with_above',
  GROUP_WITH_BELOW = 'group_with_below',
  HIDE_FROM_CANDIDATE = 'hide_from_candidate',
  SHOW_TO_CANDIDATE = 'show_to_candidate',
}

type Props = {
  index: number;
  onMenuSelect: (option: InterviewMenuOptions) => void;
};

type Fragments = {
  jobStageInterview: MoreMenuOptions_JobStageInterviewFragment;
  jobStageInterviewGroups: MoreMenuOptions_JobStageInterviewGroupsFragment[];
};

const MoreMenuOptions: FCWithFragments<Fragments, Props> = ({
  index,
  jobStageInterview,
  jobStageInterviewGroups,
  onMenuSelect,
}): JSX.Element => {
  const { t } = useTranslation();
  const moreOptionsAnchorEl = useRef<HTMLButtonElement>(null);
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  const group = jobStageInterviewGroups.find((g) =>
    g.jobStageInterviews?.some((interview) => interview.id === jobStageInterview.id)
  );
  const firstGroup = jobStageInterviewGroups.length > 0 ? jobStageInterviewGroups[0] : undefined;
  const lastGroup =
    jobStageInterviewGroups.length > 0 ? jobStageInterviewGroups[jobStageInterviewGroups.length - 1] : undefined;
  const groupIndex = jobStageInterviewGroups.findIndex((g) => g.id === group?.id);

  const options = useMemo(() => {
    /**
     * Default header as options
     */
    const menuOptions: MenuOption[] = [];

    /**
     * Show hide / show interview to candidate option
     */

    if (jobStageInterview.interviewType === InterviewType.Interview) {
      menuOptions.push({
        isSubheader: true,
        value: t('hiring-dashboard-interviewers:interviewers.plan.moreMenu.options'),
        id: 'more-menu-options-divider-1',
      });
      if (jobStageInterview.isHiddenFromCandidate) {
        menuOptions.push({
          id: InterviewMenuOptions.SHOW_TO_CANDIDATE,
          value: t('hiring-dashboard-interviewers:interviewers.plan.moreMenu.showToCandidate'),
          icon: <EyeIcon />,
        });
      } else {
        menuOptions.push({
          id: InterviewMenuOptions.HIDE_FROM_CANDIDATE,
          value: t('hiring-dashboard-interviewers:interviewers.plan.moreMenu.hideFromCandidate'),
          icon: <EyeCrossedIcon />,
        });
      }
      menuOptions.push({ isDivider: true, id: 'more-menu-options-divider-2' });
    }

    /**
     * Divider & header for organize options
     */
    menuOptions.push({
      isSubheader: true,
      value: t('hiring-dashboard-interviewers:interviewers.plan.moreMenu.organize'),
      id: 'more-menu-options-subheader-organize',
    });

    /**
     * Disabled if slot is:
     * 1. First item in a group with more than one interview
     * 2. First item of first group.
     */
    menuOptions.push({
      id: InterviewMenuOptions.MOVE_UP,
      value: t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.moveUp'),
      disabled: Boolean(
        (group?.jobStageInterviews && group.jobStageInterviews.length > 1 && index === 0) ||
        (firstGroup?.jobStageInterviews?.length &&
          firstGroup.jobStageInterviews[0].id === jobStageInterview.id &&
          groupIndex === 0)
      ),
    });

    /**
     * Disabled if slot is:
     * If it is the last item in group with more than one interview.
     * If it is the last item in last group.
     */
    menuOptions.push({
      id: InterviewMenuOptions.MOVE_DOWN,
      value: t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.moveDown'),
      disabled: Boolean(
        (group?.jobStageInterviews &&
          group.jobStageInterviews.length > 1 &&
          group.jobStageInterviews[group.jobStageInterviews.length - 1].id === jobStageInterview.id) ||
        (lastGroup &&
          lastGroup.jobStageInterviews &&
          lastGroup.jobStageInterviews.length >= 1 &&
          lastGroup.jobStageInterviews[lastGroup.jobStageInterviews.length - 1].id === jobStageInterview.id)
      ),
    });

    /**
     * Show only if it is the only item in group
     */
    if (group?.jobStageInterviews?.length === 1) {
      /**
       * Disabled for first group
       */
      menuOptions.push({
        id: InterviewMenuOptions.GROUP_WITH_ABOVE,
        value: t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.groupWithAbove'),
        disabled: firstGroup && firstGroup.id === group.id,
      });
    }

    /**
     * Show only if it is the only item in group
     */
    if (group?.jobStageInterviews?.length === 1) {
      /**
       * Disabled for last group
       */
      menuOptions.push({
        id: InterviewMenuOptions.GROUP_WITH_BELOW,
        value: t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.groupWithBelow'),
        disabled: lastGroup && lastGroup.id === group.id,
      });
    }

    return menuOptions;
  }, [
    jobStageInterview.interviewType,
    jobStageInterview.id,
    jobStageInterview.isHiddenFromCandidate,
    group?.jobStageInterviews,
    group?.id,
    index,
    firstGroup,
    groupIndex,
    lastGroup,
    t,
  ]);

  const toggleMoreOptionsMenu = () => setShowMoreOptions(!showMoreOptions);

  const handleSelect = (option: MenuOption) => {
    if (!option.id) {
      return;
    }

    toggleMoreOptionsMenu();
    onMenuSelect(option.id as InterviewMenuOptions);
  };

  return (
    <>
      <Tooltip title={t('hiring-dashboard-interviewers:interviewers.plan.groupMenu.moreOptions')}>
        <IconButton ref={moreOptionsAnchorEl} onClick={toggleMoreOptionsMenu}>
          <MoreIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="interview-menu-options"
        anchorEl={moreOptionsAnchorEl.current}
        open={showMoreOptions}
        disableScrollLock
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={toggleMoreOptionsMenu}
      >
        {options.map((option) => {
          if (option.isSubheader) {
            return <ListSubheader key={option.id}>{option.value}</ListSubheader>;
          }
          if (option.isDivider) {
            return <Divider key={option.id} />;
          }
          return (
            <MenuItem key={option.id} onClick={() => handleSelect(option)} disabled={option.disabled}>
              {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
              {option.value}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

MoreMenuOptions.fragments = {
  jobStageInterview: gql`
    fragment MoreMenuOptions_jobStageInterview on JobStageInterview {
      id
      interviewType
      jobStageInterviewGroupId
      isHiddenFromCandidate
    }
  `,
  jobStageInterviewGroups: gql`
    fragment MoreMenuOptions_jobStageInterviewGroups on JobStageInterviewGroup {
      id
      jobStageInterviews {
        id
      }
    }
  `,
};

export default MoreMenuOptions;
